import { formatMoney } from '@theme/money-formatting';
import { addToCart } from '@theme/kyn-cart-add';

/**
 * Guest wishlist: no account needed. The list lives in this browser's localStorage as
 * [{ handle, variant }] and every change is broadcast as `kyn:wishlist-change` (other tabs get
 * it through the `storage` event). Components:
 *   <kyn-wishlist-toggle>  add/remove button on the product page
 *   <kyn-wishlist-count>   item count in the header
 *   <kyn-wishlist-page>    the /pages/wishlist list with add to cart and remove
 */

const STORAGE_KEY = 'kyn:wishlist';
const CHANGE_EVENT = 'kyn:wishlist-change';
const ADDED_RESET_MS = 2500;

/** @type {{ handle: string, variant?: number }[]} Used when localStorage is unavailable. */
let memoryList = [];

/** @returns {{ handle: string, variant?: number }[]} */
function read() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => item && typeof item.handle === 'string') : [];
  } catch {
    return memoryList;
  }
}

/** @param {{ handle: string, variant?: number }[]} list */
function write(list) {
  memoryList = list;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Private mode or blocked storage: the list lasts for this page view only.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

const wishlist = {
  items: read,
  /** @param {string} handle */
  has: (handle) => read().some((item) => item.handle === handle),
  /** @param {string} handle @param {number} [variant] */
  add(handle, variant) {
    const list = read().filter((item) => item.handle !== handle);
    list.unshift(variant ? { handle, variant } : { handle });
    write(list);
  },
  /** Updates the remembered variant in place, keeping the item's position. @param {string} handle @param {number} variant */
  setVariant(handle, variant) {
    write(read().map((item) => (item.handle === handle ? { handle, variant } : item)));
  },
  /** @param {string} handle */
  remove(handle) {
    write(read().filter((item) => item.handle !== handle));
  },
};

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
});

/** Root-relative path that keeps the locale prefix ('/en') on the secondary language. */
function localePath(path) {
  const root = window.Shopify?.routes?.root ?? '/';
  return `${root.replace(/\/$/, '')}${path}`;
}

class KynWishlistToggle extends HTMLElement {
  #onChange = () => this.#render();

  connectedCallback() {
    this.button?.addEventListener('click', this.#onClick);
    window.addEventListener(CHANGE_EVENT, this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    window.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  get button() {
    return this.querySelector('button');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // On product cards the whole card navigates on click; keep the heart from triggering it.
    event.preventDefault();
    event.stopPropagation();
    const handle = this.dataset.handle;
    if (!handle) return;
    if (wishlist.has(handle)) {
      wishlist.remove(handle);
      return;
    }
    let variant = 0;
    if (this.dataset.variantSource === 'form') {
      // Product page: remember the variant currently chosen in the product form.
      const scope = this.closest('.shopify-section') ?? document;
      const input = scope.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) variant = Number(input.value);
    }
    wishlist.add(handle, variant || undefined);
  };

  #render() {
    const saved = wishlist.has(this.dataset.handle ?? '');
    this.button?.setAttribute('aria-pressed', saved ? 'true' : 'false');
    const text = (saved ? this.dataset.labelSaved : this.dataset.labelAdd) ?? '';
    const label = this.querySelector('[data-wish-label]');
    if (label) label.textContent = text;
    // Icon-only variant (product cards) announces the state through aria-label.
    if (!label) this.button?.setAttribute('aria-label', text);
  }
}

class KynWishlistCount extends HTMLElement {
  // Fabric morphs some header markup (e.g. the mobile menu) back to the server HTML, which wipes
  // the count; watching our own attribute puts it back.
  static observedAttributes = ['data-count', 'data-label'];

  #onChange = () => this.#render();

  #rendering = false;

  attributeChangedCallback() {
    if (this.#rendering) return;
    const count = wishlist.items().length;
    if (this.dataset.count !== String(count) || this.dataset.label !== this.#label(count)) this.#render();
  }

  /** @param {number} count */
  #label(count) {
    if (count === 0) return '';
    return this.dataset.format === 'paren' ? ` (${count})` : String(count);
  }

  connectedCallback() {
    window.addEventListener(CHANGE_EVENT, this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    window.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  #render() {
    // Shown through CSS (content: attr(data-label)) so a morph of the text can't erase it.
    const count = wishlist.items().length;
    const label = this.#label(count);
    this.#rendering = true;
    if (this.dataset.count !== String(count)) this.dataset.count = String(count);
    if (this.dataset.label !== label) this.dataset.label = label;
    this.#rendering = false;
  }
}

/**
 * <kyn-wishlist-page> — renders the saved products with live stock.
 * Product data comes from /products/<handle>?view=wishlist (templates/product.wishlist.liquid):
 * title in the current language, and per variant `available` plus `stock` (null when inventory
 * isn't tracked). Stock is re-read when the tab becomes visible again and right before adding to
 * the cart, taking into account what is already in the cart.
 */
class KynWishlistPage extends HTMLElement {
  /** Re-render only when the set of products changes, not when a card's variant does. */
  #renderedKey = '';
  /** @type {Map<string, any>} product JSON by handle */
  #products = new Map();

  #onChange = () => {
    if (this.#key() !== this.#renderedKey) this.#render();
  };

  #onVisible = () => {
    if (document.visibilityState !== 'visible' || wishlist.items().length === 0) return;
    this.#products.clear();
    this.#render();
  };

  connectedCallback() {
    window.addEventListener(CHANGE_EVENT, this.#onChange);
    document.addEventListener('visibilitychange', this.#onVisible);
    this.#render();
  }

  disconnectedCallback() {
    window.removeEventListener(CHANGE_EVENT, this.#onChange);
    document.removeEventListener('visibilitychange', this.#onVisible);
  }

  #key() {
    return wishlist
      .items()
      .map((item) => item.handle)
      .join('|');
  }

  get #lowStock() {
    return Number(this.dataset.lowStock) || 3;
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'PLN');
  }

  /** "Only n left" in the language's plural form. @param {number} count */
  #lowText(count) {
    const category = new Intl.PluralRules(document.documentElement.lang || 'en').select(count);
    const templates = {
      one: this.dataset.lowOne,
      few: this.dataset.lowFew,
      many: this.dataset.lowMany,
      other: this.dataset.lowOther,
    };
    return (templates[category] ?? templates.other ?? '').replace('__COUNT__', String(count));
  }

  /** @param {any} variant @returns {{ state: 'in' | 'low' | 'out', text: string }} */
  #stock(variant) {
    if (!variant?.available) return { state: 'out', text: this.dataset.labelSoldOut ?? '' };
    if (variant.stock !== null && variant.stock !== undefined && variant.stock <= this.#lowStock) {
      return { state: 'low', text: this.#lowText(variant.stock) };
    }
    return { state: 'in', text: this.dataset.labelInStock ?? '' };
  }

  /** @param {string} handle @param {boolean} [fresh] bypass the page and HTTP caches */
  async #fetchProduct(handle, fresh = false) {
    if (!fresh && this.#products.has(handle)) return this.#products.get(handle);
    const bust = fresh ? `&_=${Date.now()}` : '';
    // No "Accept: application/json" here: with it Shopify answers with its own product JSON
    // and ignores ?view=wishlist.
    const response = await fetch(localePath(`/products/${encodeURIComponent(handle)}?view=wishlist${bust}`), {
      cache: fresh ? 'no-store' : 'default',
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Product ${handle}: ${response.status}`);
    const product = JSON.parse(await response.text());
    this.#products.set(handle, product);
    return product;
  }

  /** @param {number} variantId */
  async #quantityInCart(variantId) {
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      return cart.items
        .filter((line) => line.variant_id === variantId)
        .reduce((sum, line) => sum + line.quantity, 0);
    } catch {
      return 0;
    }
  }

  async #render() {
    const items = wishlist.items();
    this.#renderedKey = this.#key();
    const grid = this.querySelector('[data-wish-grid]');
    const empty = this.querySelector('[data-wish-empty]');
    const template = this.querySelector('template[data-wish-card]');
    if (!(grid instanceof HTMLElement) || !(empty instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    const results = await Promise.all(
      items.map(async (item) => {
        try {
          return { item, product: await this.#fetchProduct(item.handle) };
        } catch (error) {
          console.error(error);
          return { item, product: undefined };
        }
      })
    );

    // Products that no longer exist are dropped from the list.
    const gone = results.filter((r) => r.product === null).map((r) => r.item.handle);
    if (gone.length > 0) {
      for (const handle of gone) wishlist.remove(handle);
      return;
    }

    grid.replaceChildren(
      ...results.filter((r) => r.product).map(({ item, product }) => this.#card(template, item, product))
    );
    const isEmpty = grid.children.length === 0;
    grid.hidden = isEmpty;
    empty.hidden = !isEmpty;
    this.dataset.state = isEmpty ? 'empty' : 'list';
  }

  /**
   * @param {HTMLTemplateElement} template
   * @param {{ handle: string, variant?: number }} item
   * @param {any} product
   */
  #card(template, item, product) {
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const card = /** @type {HTMLElement} */ (fragment.firstElementChild);
    const url = product.url ?? localePath(`/products/${product.handle}`);
    /** @param {string} selector */
    const $ = (selector) => card.querySelector(selector);

    for (const link of card.querySelectorAll('[data-wish-link], [data-wish-notify]')) link.setAttribute('href', url);

    const image = $('[data-wish-image]');
    if (image instanceof HTMLImageElement && product.image) {
      const raw = product.image.startsWith('//') ? `https:${product.image}` : product.image;
      const base = raw.replace(/([?&])width=\d+(&|$)/, '$1').replace(/[?&]$/, '');
      const sized = (width) => `${base}${base.includes('?') ? '&' : '?'}width=${width}`;
      image.src = sized(600);
      image.srcset = `${sized(360)} 360w, ${sized(600)} 600w, ${sized(900)} 900w`;
      image.alt = product.title;
    } else if (image) {
      image.remove();
    }

    const title = $('[data-wish-title]');
    if (title) title.textContent = product.title;

    const price = $('[data-wish-price]');
    const select = $('[data-wish-variant]');
    const add = $('[data-wish-add]');
    const addLabel = $('[data-wish-add-label]');
    const stockLine = $('[data-wish-stock]');
    const note = $('[data-wish-note]');
    const notify = $('[data-wish-notify]');
    const badge = $('[data-wish-badge]');
    const message = $('[data-wish-message]');

    let variants = product.variants ?? [];
    const saved = variants.find((v) => v.id === item.variant);
    const pick = () =>
      variants.find((v) => v.id === item.variant && v.available) ?? variants.find((v) => v.available) ?? variants[0];
    let selectedId = pick()?.id;

    // The saved size ran out: say so, and offer the first size that is still in stock.
    if (saved && !saved.available && product.available && note instanceof HTMLElement) {
      note.textContent = (this.dataset.labelSavedOut ?? '').replace('__VARIANT__', saved.title);
      note.hidden = false;
    }

    const fillOptions = () => {
      if (!(select instanceof HTMLSelectElement)) return;
      if (variants.length <= 1) {
        select.closest('[data-wish-variant-field]')?.remove();
        return;
      }
      select.replaceChildren(
        ...variants.map((variant) => {
          const stock = this.#stock(variant);
          const text = stock.state === 'in' ? variant.title : `${variant.title} — ${stock.text}`;
          const option = new Option(text, String(variant.id), false, variant.id === selectedId);
          option.disabled = !variant.available;
          return option;
        })
      );
    };

    const selected = () => variants.find((v) => v.id === selectedId) ?? pick();

    const refresh = () => {
      const variant = selected();
      const stock = this.#stock(variant);
      if (price) price.textContent = variant ? this.#money(variant.price) : '';
      if (stockLine instanceof HTMLElement) {
        stockLine.textContent = stock.text;
        stockLine.dataset.state = stock.state;
      }
      const soldOut = !variants.some((v) => v.available);
      card.dataset.soldOut = String(soldOut);
      if (badge instanceof HTMLElement) badge.hidden = !soldOut;
      if (notify instanceof HTMLElement) notify.hidden = !soldOut;
      if (add instanceof HTMLButtonElement) add.disabled = stock.state === 'out';
      if (addLabel) {
        addLabel.textContent = stock.state === 'out' ? this.dataset.labelSoldOut ?? '' : this.dataset.labelAdd ?? '';
      }
    };

    /** @param {string} text */
    const showMessage = (text) => {
      if (message instanceof HTMLElement) {
        message.textContent = text;
        message.hidden = !text;
      }
    };

    fillOptions();
    refresh();

    select?.addEventListener('change', () => {
      selectedId = Number(/** @type {HTMLSelectElement} */ (select).value);
      wishlist.setVariant(product.handle, selectedId);
      showMessage('');
      refresh();
    });

    add?.addEventListener('click', async () => {
      if (!(add instanceof HTMLButtonElement)) return;
      add.disabled = true;
      showMessage('');

      // Stock may have changed since the page loaded: re-read it and count what is already in the cart.
      try {
        const fresh = await this.#fetchProduct(product.handle, true);
        if (fresh?.variants) variants = fresh.variants;
      } catch (error) {
        console.error(error);
      }
      const variant = selected();
      const inCart = variant ? await this.#quantityInCart(variant.id) : 0;
      const left = variant?.stock === null || variant?.stock === undefined ? Infinity : variant.stock - inCart;

      if (!variant?.available || left <= 0) {
        fillOptions();
        refresh();
        const allInCart = inCart > 0 && variant?.available;
        showMessage((allInCart ? this.dataset.labelAllInCart : this.dataset.labelNoLongerInStock) ?? '');
        return;
      }

      const added = await addToCart(this, [{ id: variant.id, quantity: 1 }], 'kyn-wishlist');
      if (added) {
        if (addLabel) addLabel.textContent = this.dataset.labelAdded ?? '';
        window.setTimeout(refresh, ADDED_RESET_MS);
        add.disabled = false;
      } else {
        showMessage(this.dataset.labelAddError ?? '');
        refresh();
      }
    });

    $('[data-wish-remove]')?.addEventListener('click', () => wishlist.remove(product.handle));

    return card;
  }
}

if (!customElements.get('kyn-wishlist-toggle')) customElements.define('kyn-wishlist-toggle', KynWishlistToggle);
if (!customElements.get('kyn-wishlist-count')) customElements.define('kyn-wishlist-count', KynWishlistCount);
if (!customElements.get('kyn-wishlist-page')) customElements.define('kyn-wishlist-page', KynWishlistPage);
